import { memo, useEffect, useImperativeHandle, useRef, useState } from 'react'
import type { Ref } from 'react'

import { useGameStore } from '../store/gameStore'
import { tierFor } from './bigWinTiers'
import type { Tier } from './bigWinTiers'

/**
 * Big Win celebration overlay.
 *
 * Appears when a round's total win crosses the big-win threshold: layered
 * flames rush in from the sides and meet in the centre, then an odometer
 * counts the win up while each digit rolls down into place. When the count
 * settles and the server has a Risk Game offer on the table, the overlay
 * turns into the choice itself — collect, or carry the win to the gamble.
 *
 * The amount shown is the server's `winCoins`; the count-up is pure
 * presentation. Tier thresholds are multiples of the round's charge, the
 * industry-standard way "big" is defined (win relative to bet, not coins).
 */

/** Count-up duration: bigger tiers earn a longer drum roll. */
const COUNT_MS: Record<string, number> = {
  'BIG WIN': 1600,
  'MEGA WIN': 2400,
  'EPIC WIN': 3200,
}

const easeOutQuart = (t: number): number => 1 - (1 - t) ** 4

/** Imperative handle on the odometer: set the shown value without a render. */
export interface OdometerHandle {
  set: (value: number) => void
}

/**
 * Odometer for the count-up. The column layout is fixed from the FINAL
 * amount (so the row never re-flows while counting), and the value shown is
 * written straight into the DOM: one `transform` per digit strip, one
 * `visibility` per column. Nothing here re-renders during the count — the
 * previous version called setState sixty times a second, which re-diffed
 * the whole overlay (flames included) on every frame of a 3-second roll.
 *
 * Each digit column is a viewport one character tall; the strip of 0–9
 * behind it slides so the old digit exits downward and the new one drops in
 * from above. Thousands separators fall on the same columns for any value
 * that is right-aligned under the target, so a shorter current value just
 * hides its leading columns.
 */
function Odometer({ target, ref }: { target: number; ref: Ref<OdometerHandle> }): React.JSX.Element {
  const layout = target.toLocaleString('en-US')
  const cols = useRef<(HTMLSpanElement | null)[]>([])
  const strips = useRef<(HTMLSpanElement | null)[]>([])
  const root = useRef<HTMLSpanElement>(null)

  useImperativeHandle(
    ref,
    () => ({
      set(value) {
        const text = value.toLocaleString('en-US')
        const n = layout.length
        const offset = n - text.length
        for (let i = 0; i < n; i++) {
          const col = cols.current[i]
          if (!col) continue
          const ch = i >= offset ? text[i - offset] : ''
          const strip = strips.current[i]
          if (strip) {
            // Digit column: hidden while the value is too short to reach it.
            col.style.visibility = ch ? 'visible' : 'hidden'
            if (ch) strip.style.transform = `translateY(${-(ch.charCodeAt(0) - 48)}em)`
          } else {
            col.style.visibility = ch ? 'visible' : 'hidden'
          }
        }
        if (root.current) root.current.setAttribute('aria-label', text)
      },
    }),
    [layout],
  )

  return (
    <span className="bigwin__amount" aria-label={layout} ref={root}>
      {[...layout].map((ch, i) =>
        ch >= '0' && ch <= '9' ? (
          <span
            key={i}
            className="bigwin__digit"
            style={{ visibility: 'hidden' }}
            ref={(el) => {
              cols.current[i] = el
            }}
          >
            <span
              className="bigwin__digit-strip"
              style={{ transform: 'translateY(0em)' }}
              ref={(el) => {
                strips.current[i] = el
              }}
            >
              {Array.from({ length: 10 }, (_, d) => (
                <span key={d}>{d}</span>
              ))}
            </span>
          </span>
        ) : (
          <span
            key={i}
            className="bigwin__sep"
            style={{ visibility: 'hidden' }}
            ref={(el) => {
              cols.current[i] = el
              strips.current[i] = null
            }}
          >
            {ch}
          </span>
        ),
      )}
    </span>
  )
}

/**
 * A wall of fire, three tiled layers back to front: red spike tips behind,
 * orange tongues in the middle, a solid yellow base in front. Each layer is
 * a 300-wide tile repeated three times across a 900-wide SVG; every layer's
 * silhouette is closed down to the bottom edge, so the wall is continuous —
 * no dark gaps show through behind the counter. CSS does motion and glow.
 */
const Flames = memo(function Flames(): React.JSX.Element {
  // Tile silhouettes, x 0..300, bottom edge y=340. Edges meet at the same
  // height on both sides so three copies tile without a visible seam.
  // Tongues are S-curved: each rises with a lateral swing, narrows into a
  // leaning tip and comes down with the opposite curvature — a licking
  // flame, not a triangular spike.
  const red =
    'M0 340 L0 250 C14 238 22 210 18 176 C14 142 26 118 22 66 C30 110 36 150 46 180 ' +
    'C52 200 60 224 68 242 C80 226 84 194 80 158 C74 116 94 92 88 34 C96 82 104 130 112 168 ' +
    'C118 196 126 220 134 244 C146 230 148 204 144 176 C138 140 154 118 150 78 C156 116 162 152 170 186 ' +
    'C176 212 184 232 192 246 C204 232 206 200 202 166 C196 128 214 104 210 54 C218 100 226 144 234 176 ' +
    'C240 202 250 224 258 240 C270 228 274 210 272 190 C280 210 292 234 300 250 L300 340 Z'
  const orange =
    'M0 340 L0 268 C12 256 18 236 14 212 C10 186 22 168 18 126 C26 164 32 192 42 216 ' +
    'C50 234 58 248 66 258 C78 244 80 218 76 192 C70 158 86 140 82 96 C90 134 98 168 106 198 ' +
    'C112 220 120 240 128 254 C140 242 142 220 138 196 C132 166 148 148 144 112 C152 146 158 176 166 202 ' +
    'C172 222 180 240 188 254 C200 240 202 216 198 190 C192 158 208 140 204 100 C212 138 220 172 228 200 ' +
    'C236 226 246 246 256 256 C268 246 272 232 270 216 C278 234 290 254 300 268 L300 340 Z'
  const yellow =
    'M0 340 L0 290 C10 280 16 264 12 246 C8 226 18 212 16 180 C22 210 28 232 36 252 ' +
    'C44 268 52 278 60 284 C70 272 72 252 68 232 C62 204 76 190 72 156 C80 186 86 212 94 234 ' +
    'C100 252 108 268 116 278 C126 268 128 250 124 230 C118 202 132 186 128 152 C136 184 142 210 150 232 ' +
    'C156 252 164 266 172 276 C182 266 184 246 180 226 C174 200 188 184 184 150 C192 182 198 208 206 230 ' +
    'C214 252 224 268 234 276 C246 268 250 254 248 238 C256 256 274 276 300 290 L300 340 Z'
  // Pale streaks inside the yellow base — curved like the tongues above.
  const streakA =
    'M92 318 C82 288 88 258 100 236 C96 268 104 292 110 316 Z'
  const streakB =
    'M226 322 C216 292 222 262 234 240 C230 272 238 296 244 320 Z'
  // Left half leans right, right half is the same tile mirrored, so every
  // tongue leans TOWARD the centre of the screen — fire converging, not
  // combed sideways. Edge heights match, so the centre seam is invisible.
  const tile = (d: string): React.JSX.Element[] => [
    <path key="l" d={d} transform="scale(1.5 1)" />,
    <path key="r" d={d} transform="translate(900 0) scale(-1.5 1)" />,
  ]
  return (
    <div className="bigwin__flames" aria-hidden="true">
      <div className="bigwin__glow" />
      <svg className="bigwin__flame bigwin__flame--back" viewBox="0 0 900 340" preserveAspectRatio="none">
        <defs>
          {/* Stops shimmer: SMIL runs fine here because the SVG is live DOM
              (unlike board symbols, which are rasterised into textures). */}
          <linearGradient id="bw-red" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0" stopColor="#ff9a1f">
              <animate attributeName="stop-color" values="#ff9a1f;#ffb52e;#ff8514;#ff9a1f" dur="2.6s" repeatCount="indefinite" />
            </stop>
            <stop offset="0.55" stopColor="#f2600f">
              <animate attributeName="stop-color" values="#f2600f;#ff7a1a;#e8500c;#f2600f" dur="2.1s" repeatCount="indefinite" />
            </stop>
            <stop offset="1" stopColor="#e02414">
              <animate attributeName="stop-color" values="#e02414;#ff4a1c;#c81d11;#e02414" dur="2.9s" repeatCount="indefinite" />
            </stop>
          </linearGradient>
        </defs>
        <g fill="url(#bw-red)">{tile(red)}</g>
      </svg>
      <svg className="bigwin__flame bigwin__flame--mid" viewBox="0 0 900 340" preserveAspectRatio="none">
        <defs>
          <linearGradient id="bw-orange" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0" stopColor="#ffd23f">
              <animate attributeName="stop-color" values="#ffd23f;#ffe14f;#ffc22e;#ffd23f" dur="1.9s" repeatCount="indefinite" />
            </stop>
            <stop offset="1" stopColor="#ff7a12">
              <animate attributeName="stop-color" values="#ff7a12;#ff9a1f;#f2600f;#ff7a12" dur="2.4s" repeatCount="indefinite" />
            </stop>
          </linearGradient>
        </defs>
        <g fill="url(#bw-orange)">{tile(orange)}</g>
      </svg>
      <svg className="bigwin__flame bigwin__flame--front" viewBox="0 0 900 340" preserveAspectRatio="none">
        <defs>
          <linearGradient id="bw-yellow" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0" stopColor="#fff06a">
              <animate attributeName="stop-color" values="#fff06a;#fff8a0;#ffe74f;#fff06a" dur="1.6s" repeatCount="indefinite" />
            </stop>
            <stop offset="1" stopColor="#ffc61a">
              <animate attributeName="stop-color" values="#ffc61a;#ffd23f;#ffb514;#ffc61a" dur="2.2s" repeatCount="indefinite" />
            </stop>
          </linearGradient>
        </defs>
        <g fill="url(#bw-yellow)">{tile(yellow)}</g>
        <g fill="#fff3a0" opacity="0.85">
          {tile(streakA)}
          {tile(streakB)}
        </g>
      </svg>
    </div>
  )
})

/**
 * The other celebration: light instead of fire.
 *
 * Everything here radiates from ONE point — the centre, where the amount
 * sits — because that is the brief: the win is the light source, not
 * something lit by a fire standing beside it. So there is no wall, no
 * silhouette, nothing with an edge of its own; the whole screen is a
 * gradient whose origin is the number.
 *
 * Three layers, each doing a job the other two cannot:
 *
 * 1. `--wash` — a full-viewport radial gradient, gold at the middle
 *    falling to nothing at the corners. This is the "світіння на весь
 *    екран": it dims the game without a grey scrim, which is what keeps
 *    the celebration warm rather than modal.
 * 2. `--rays` — two counter-rotating conic fans of gold spokes. Rotation
 *    is what stops a static sunburst reading as a printed badge, and
 *    counter-rotation is what stops the pair reading as one turning
 *    wheel: the crossings travel, and travelling crossings are the
 *    "переливається" the brief asks for.
 * 3. `--bloom` — a wide, soft, hot body between the core and the rim. It
 *    exists because rays alone read as empty: a fan of spokes over a thin
 *    wash is a printed sunburst, while spokes over a bright body are light
 *    breaking through it. This is the layer that gives the effect volume.
 * 4. `--core` — a small, very bright, breathing disc directly under the
 *    digits, so the beam visibly starts AT the number.
 * 5. `--sparks` — glints and motes. The gradients above have no grain of
 *    their own, and light with no grain looks flat no matter how bright it
 *    is; the sparkles are what make the air in front of the beam read as
 *    full rather than as an empty gold panel.
 *
 * All CSS gradients and transforms: no SVG paths, because there is no
 * shape to describe, and no canvas, because this is one composited layer
 * per element and the GPU does it for free.
 */

/**
 * Sparkle placement.
 *
 * Deterministic (a small LCG, fixed seed) rather than `Math.random()`: the
 * layout is generated once per tier and must be identical on every open,
 * so nobody has to debug a celebration that only looks wrong sometimes.
 *
 * Positions are polar around the centre — the light source — so glints
 * cluster where the beam is hot and thin out toward the corners, which is
 * how airborne dust actually catches a spotlight. A uniform scatter over
 * the rectangle would put as many sparks in the dark corners as on the
 * number.
 */
interface Spark {
  x: number
  y: number
  size: number
  delay: number
  dur: number
  drift: number
  mote: boolean
}

function makeSparks(count: number): Spark[] {
  let seed = 0x9e3779b9
  const rnd = (): number => {
    seed = (seed * 1664525 + 1013904223) >>> 0
    return seed / 0x100000000
  }
  return Array.from({ length: count }, (_, i) => {
    const angle = rnd() * 360
    // sqrt keeps the density per unit AREA even instead of piling every
    // spark onto the centre. The inner hole is not decorative: the middle
    // of the screen is a near-white core, and a white glint screen-blended
    // onto white is invisible — sparks only read where there is still some
    // colour left to add light to.
    const radius = 16 + Math.sqrt(rnd()) * 44
    const mote = i % 3 === 0
    return {
      x: 50 + Math.cos((angle * Math.PI) / 180) * radius,
      y: 50 + Math.sin((angle * Math.PI) / 180) * radius * 0.82,
      size: mote ? 5 + rnd() * 7 : 16 + rnd() * 26,
      delay: rnd() * 2.6,
      dur: mote ? 3.4 + rnd() * 2.6 : 1.3 + rnd() * 1.4,
      drift: -14 - rnd() * 46,
      mote,
    }
  })
}

/**
 * One falling gem. Depth is the whole trick: a curtain of identically sized
 * shards reads as wallpaper, while a spread of size, speed and opacity reads
 * as a volume of them between the viewer and the light.
 */
interface Crystal {
  x: number
  size: number
  delay: number
  dur: number
  drift: number
  spin: number
  tilt: number
  red: boolean
  depth: number
}

/**
 * Counts climb with the tier; the colour mix does not.
 *
 * An even split at every tier, so the ladder is carried by how MUCH falls
 * rather than by what colour it is. The two gems are the crest's own ruby
 * and sapphire, and holding one of them back read as a missing colour rather
 * than as a quieter tier.
 */
const CRYSTAL_COUNTS: Record<string, number> = { big: 21, mega: 36, epic: 57 }
const CRYSTAL_RED: Record<string, number> = { big: 0.5, mega: 0.5, epic: 0.5 }

function makeCrystals(count: number, redShare: number): Crystal[] {
  let seed = 0x85ebca6b
  const rnd = (): number => {
    seed = (seed * 1664525 + 1013904223) >>> 0
    return seed / 0x100000000
  }
  return Array.from({ length: count }, () => {
    // Squared, so most gems sit far away and only a few pass close: an even
    // spread of depth puts as many big ones in front as small ones behind,
    // and the layer stops reading as distance.
    const depth = rnd() ** 2
    return {
      x: -6 + rnd() * 112,
      size: 16 + depth * 46,
      delay: -rnd() * 9,
      // Near gems fall faster. Far ones drifting at the same speed is the
      // single thing that kills a parallax.
      dur: 9.5 - depth * 4.4 + rnd() * 1.6,
      drift: (rnd() - 0.5) * 150,
      spin: (rnd() < 0.5 ? -1 : 1) * (140 + rnd() * 420),
      tilt: rnd() * 360,
      red: rnd() < redShare,
      depth,
    }
  })
}

const crystalCache = new Map<string, Crystal[]>()

function crystalsFor(slug: string): Crystal[] {
  let list = crystalCache.get(slug)
  if (!list) {
    list = makeCrystals(CRYSTAL_COUNTS[slug] ?? 0, CRYSTAL_RED[slug] ?? 0)
    crystalCache.set(slug, list)
  }
  return list
}

/** One layout per tier, built on first use and kept for the session. */
const SPARK_COUNTS: Record<string, number> = { big: 22, mega: 34, epic: 50 }
const sparkCache = new Map<string, Spark[]>()

function sparksFor(slug: string): Spark[] {
  let list = sparkCache.get(slug)
  if (!list) {
    list = makeSparks(SPARK_COUNTS[slug] ?? 26)
    sparkCache.set(slug, list)
  }
  return list
}

/**
 * Gems raining behind the rays.
 *
 * Placed between the wash and the ray fans on purpose: in front of the rays
 * they would read as confetti stuck to the lens, behind them they read as
 * something falling through the light. Only `transform` and `opacity` are
 * animated, so the whole curtain stays on the compositor and never repaints.
 */
const CrystalRain = memo(function CrystalRain({ slug }: { slug: string }): React.JSX.Element | null {
  const crystals = useGameStore((s) => s.theme.winCrystals)
  if (!crystals.red && !crystals.blue) return null
  const list = crystalsFor(slug)
  if (list.length === 0) return null
  return (
    <div className="bigwin__rain" aria-hidden="true">
      {list.map((c, i) => {
        const src = c.red ? (crystals.red ?? crystals.blue) : (crystals.blue ?? crystals.red)
        if (!src) return null
        return (
          <img
            key={i}
            className="bigwin__gem"
            src={src}
            alt=""
            draggable={false}
            style={
              {
                left: `${c.x}%`,
                width: `${c.size}px`,
                opacity: 0.24 + c.depth * 0.6,
                '--gem-delay': `${c.delay}s`,
                '--gem-dur': `${c.dur}s`,
                '--gem-drift': `${c.drift}px`,
                '--gem-spin': `${c.spin}deg`,
                '--gem-tilt': `${c.tilt}deg`,
              } as React.CSSProperties
            }
          />
        )
      })}
    </div>
  )
})

const Sunburst = memo(function Sunburst({ slug }: { slug: string }): React.JSX.Element {
  return (
    <div className="bigwin__sun" aria-hidden="true">
      <div className="bigwin__sun-wash" />
      <CrystalRain slug={slug} />
      <div className="bigwin__sun-rays bigwin__sun-rays--cw" />
      <div className="bigwin__sun-rays bigwin__sun-rays--ccw" />
      <div className="bigwin__sun-bloom" />
      <div className="bigwin__sun-core" />
      <div className="bigwin__sparks">
        {sparksFor(slug).map((s, i) => (
          <span
            key={i}
            className={`bigwin__spark${s.mote ? ' bigwin__spark--mote' : ''}`}
            style={
              {
                left: `${s.x}%`,
                top: `${s.y}%`,
                '--sp-size': `${s.size}px`,
                '--sp-delay': `${s.delay}s`,
                '--sp-dur': `${s.dur}s`,
                '--sp-drift': `${s.drift}px`,
              } as React.CSSProperties
            }
          />
        ))}
      </div>
    </div>
  )
})

export function BigWinOverlay(): React.JSX.Element | null {
  const phase = useGameStore((s) => s.phase)
  const lastRound = useGameStore((s) => s.lastRound)
  const gambleOffer = useGameStore((s) => s.gambleOffer)
  const gambleBusy = useGameStore((s) => s.gambleBusy)
  const gambleCollect = useGameStore((s) => s.gambleCollect)
  const setCelebrating = useGameStore((s) => s.setCelebrating)
  const celebration = useGameStore((s) => s.theme.stage.celebration)
  const winTitles = useGameStore((s) => s.theme.winTitles)
  const winButton = useGameStore((s) => s.theme.winButton)

  const [shown, setShown] = useState<{ roundId: string; tier: Tier; win: number } | null>(null)
  const [settled, setSettled] = useState(false)
  const seenRound = useRef<string | null>(null)
  const raf = useRef(0)
  const odometer = useRef<OdometerHandle>(null)

  // Open once per round, the moment the reels have finished (phase back to idle).
  useEffect(() => {
    if (phase !== 'idle' || !lastRound || seenRound.current === lastRound.roundId) return
    seenRound.current = lastRound.roundId
    const tier = tierFor(lastRound.winCoins, lastRound.chargedCoins)
    if (!tier) return
    setShown({ roundId: lastRound.roundId, tier, win: lastRound.winCoins })
    setSettled(false)
  }, [phase, lastRound])

  // Count-up: rAF drives the value straight into the odometer's DOM (no
  // React state per frame), the digit strips animate themselves via CSS.
  useEffect(() => {
    if (!shown) return
    const duration = COUNT_MS[shown.tier.label] ?? 1600
    const start = performance.now() + 350 // let the flames land first
    odometer.current?.set(0)
    const tick = (now: number): void => {
      const t = Math.min(1, Math.max(0, (now - start) / duration))
      odometer.current?.set(Math.round(shown.win * easeOutQuart(t)))
      if (t < 1) raf.current = requestAnimationFrame(tick)
      else setSettled(true)
    }
    raf.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf.current)
  }, [shown])

  // Tell the rest of the app the celebration is up — the autospin loop waits
  // on this flag instead of spinning over the overlay.
  useEffect(() => {
    setCelebrating(shown !== null)
    return () => setCelebrating(false)
  }, [shown, setCelebrating])

  // A new spin while the overlay is somehow still up (hotkey) closes it.
  useEffect(() => {
    if (phase === 'spinning') setShown(null)
  }, [phase])

  if (!shown) return null

  const offer = gambleOffer?.available ? gambleOffer : null

  const skip = (): void => {
    if (!settled) {
      cancelAnimationFrame(raf.current)
      odometer.current?.set(shown.win)
      setSettled(true)
    }
  }

  return (
    <div
      className={`bigwin bigwin--${celebration} bigwin--${shown.tier.slug}`}
      role="dialog"
      aria-label={shown.tier.label}
      onClick={settled && !offer ? () => setShown(null) : skip}
    >
      <div className="bigwin__scene">
        {celebration === 'sunburst' ? <Sunburst slug={shown.tier.slug} /> : <Flames />}
        {/* Warm halo behind the text: fills the dark pocket between flame
            tips so the numbers sit in light, not on the scrim. The sunburst
            brings its own core and switches this off. */}
        {celebration !== 'sunburst' && <div className="bigwin__halo" aria-hidden="true" />}
        {/* Drawn lettering when the theme ships it, live text otherwise. The
            alt text is the tier label either way, so a screen reader and a
            missing file both still announce which win this is. */}
        {winTitles[shown.tier.slug] ? (
          <img
            className="bigwin__title bigwin__title--art"
            src={winTitles[shown.tier.slug] as string}
            alt={shown.tier.label}
            draggable={false}
          />
        ) : (
          <div className="bigwin__title">{shown.tier.label}</div>
        )}
        <Odometer key={shown.roundId} target={shown.win} ref={odometer} />

        {settled && (
          <div className="bigwin__actions">
            {offer ? (
              <>
                <button
                  className="bigwin__btn bigwin__btn--collect"
                  type="button"
                  disabled={gambleBusy}
                  onClick={(e) => {
                    e.stopPropagation()
                    void gambleCollect().then(() => setShown(null))
                  }}
                >
                  Collect
                </button>
                <button
                  className="bigwin__btn bigwin__btn--gamble"
                  type="button"
                  disabled={gambleBusy}
                  onClick={(e) => {
                    e.stopPropagation()
                    // The colour choice lives in the Risk Game panel below.
                    setShown(null)
                  }}
                >
                  Gamble ×2
                </button>
              </>
            ) : (
              <button
                className={`bigwin__btn bigwin__btn--collect${winButton ? ' bigwin__btn--art' : ''}`}
                type="button"
                onClick={() => setShown(null)}
                // The plaque comes from the theme, so the URL cannot live in
                // the stylesheet; everything else about it does.
                style={winButton ? { backgroundImage: `url("${winButton}")` } : undefined}
              >
                Continue
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
